import { getApiClient } from "@src/api";
import { isAuthenticated, shareKeySignal } from "@src/auth/auth-utils";
import { graphql } from "@src/gql";
import {
	type DefaultError,
	type QueryKey,
	type UseQueryOptions,
	type UseSuspenseQueryOptions,
	queryOptions,
	useQuery,
	useSuspenseQuery,
} from "@tanstack/react-query";
import { type Plan, planSchema } from "../plan-schemas";
import { defaultPlan } from "./methods";
import { planQueryKeys } from "./query-keys";

const myPlanQuery = graphql(/* GraphQL */ `
	query MyPlans($shareKey: String) {
		plans {
			myPlan(shareKey: $shareKey) {
				...PlanDocumentFragment
			}
		}
	}
`);

type FetchPlanOptions = {
	shareKey?: string;
};

type MyPlanQueryOverrides = Omit<
	UseQueryOptions<Plan, DefaultError, Plan, QueryKey>,
	"queryKey" | "queryFn" | "enabled"
>;

type MyPlanSuspenseQueryOverrides = Omit<
	UseSuspenseQueryOptions<Plan, DefaultError, Plan, QueryKey>,
	"queryKey" | "queryFn"
>;

async function fetchPlanData({ shareKey }: FetchPlanOptions) {
	if (!isAuthenticated() && !shareKey) {
		return defaultPlan;
	}

	const client = getApiClient();

	const response = await client.query({
		query: myPlanQuery,
		variables: { shareKey },
	});

	const planData = response.data?.plans.myPlan;
	if (!planData) return defaultPlan;

	const parsedPlan = planSchema.safeParse(planData);
	if (!parsedPlan.success) {
		console.error("Invalid plan payload", parsedPlan.error);
		return defaultPlan;
	}

	return parsedPlan.data;
}

const myPlanQueryOptions = (
	options: FetchPlanOptions = {},
	queryOverrides?: MyPlanQueryOverrides | MyPlanSuspenseQueryOverrides,
) => {
	const shareKey = options.shareKey ?? shareKeySignal.value ?? undefined;

	return queryOptions({
		queryKey: planQueryKeys.myPlan(shareKey),
		enabled: isAuthenticated() || !!shareKey,
		staleTime: Number.POSITIVE_INFINITY,
		queryFn: () => fetchPlanData({ shareKey }),
		...queryOverrides,
	});
};

export const useMyPlanSuspense = (
	options?: FetchPlanOptions,
	queryOverrides?: MyPlanSuspenseQueryOverrides,
) => useSuspenseQuery(myPlanQueryOptions(options, queryOverrides));

export const useMyPlan = (
	options?: FetchPlanOptions,
	queryOverrides?: MyPlanQueryOverrides,
) => useQuery(myPlanQueryOptions(options, queryOverrides));
